import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PackageX, Search, X, Loader2, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 100;

const SeasonalPendingPackingReport = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingQty, setPendingQty] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async (pageArg = 1, overrides = {}) => {
    const searchArg = overrides.search !== undefined ? overrides.search : search;
    const fromArg = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
    const toArg = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
    const statusArg = overrides.status !== undefined ? overrides.status : statusFilter;
    setLoading(true);
    try {
      const r = await apiClient.get('/wms/seasonal/pending-packing-report', {
        params: {
          q: searchArg || undefined,
          date_from: fromArg || undefined,
          date_to: toArg || undefined,
          status: statusArg && statusArg !== 'all' ? statusArg : undefined,
          page: pageArg,
          page_size: PAGE_SIZE,
        },
      });
      const d = r.data || {};
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setPendingQty(d.pending_qty || 0);
      setPage(d.page || pageArg);
      setTotalPages(d.total_pages || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setRows([]); setTotal(0); setPendingQty(0); setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo, statusFilter]);

  useEffect(() => { fetchReport(1, { search: '', dateFrom: '', dateTo: '', status: 'all' }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => fetchReport(1);
  const handleClear = () => {
    setSearch(''); setDateFrom(''); setDateTo(''); setStatusFilter('all');
    fetchReport(1, { search: '', dateFrom: '', dateTo: '', status: 'all' });
  };
  const handleStatusChange = (v) => { setStatusFilter(v); fetchReport(1, { status: v }); };
  const goPage = (p) => { if (p >= 1 && p <= totalPages) fetchReport(p); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await apiClient.get('/wms/seasonal/pending-packing-report', {
        params: {
          q: search || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
          format: 'csv',
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `seasonal_pending_packing_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stat = (label, value, color) => (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
      <p className="text-xs text-[#5A5F6E]">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color || 'text-[#1A1F2E]'}`}>{value}</p>
    </div>
  );

  const cell = (v) => (v || v === 0 ? v : <span className="text-gray-300">—</span>);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-pending-packing-report-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="sppr-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <PackageX className="w-7 h-7 mr-3" />
          <div>
            <h1 className="text-xl font-bold font-display">Seasonal Pending Packing</h1>
            <p className="text-sm text-[#E8DCC8]">Generated pick slips not yet fully packed</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="pt-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Global filter</label>
              <div className="flex gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Search SP number, slug, customer, place, category, SKU code, design…"
                  className="flex-1"
                  data-testid="sppr-search-input"
                />
                <Button onClick={handleSearch} className="bg-lime-600 hover:bg-lime-700 text-white" data-testid="sppr-search-btn">
                  <Search className="w-4 h-4" />
                </Button>
                <Button onClick={handleClear} variant="outline" className="border-[#E5DFD0]" data-testid="sppr-clear-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="w-full md:w-48">
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Status</label>
                <Select value={statusFilter} onValueChange={handleStatusChange}>
                  <SelectTrigger data-testid="sppr-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All pending</SelectItem>
                    <SelectItem value="not_packed">Not Packed</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Generated from (IST)</label>
                <Input type="datetime-local" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} data-testid="sppr-date-from" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Generated to (IST)</label>
                <Input type="datetime-local" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} data-testid="sppr-date-to" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearch} variant="outline" className="border-[#E5DFD0]" data-testid="sppr-apply-btn">
                  Apply
                </Button>
                <Button onClick={handleExport} disabled={exporting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sppr-export-csv-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div data-testid="sppr-summary-total">{stat(statusFilter === 'cancelled' ? 'Cancelled SKU Lines' : 'Pending SKU Lines', total, statusFilter === 'cancelled' ? 'text-gray-700' : 'text-indigo-700')}</div>
          <div data-testid="sppr-summary-pending">{stat(statusFilter === 'cancelled' ? 'Total Cancelled Qty' : 'Total Pending Qty', pendingQty, 'text-amber-700')}</div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sppr-report-table">
                <thead>
                  <tr className="text-left text-[#5A5F6E] bg-gray-50 border-b whitespace-nowrap">
                    <th className="px-3 py-2.5 font-medium">Pick Slip #</th>
                    <th className="px-3 py-2.5 font-medium">Slug</th>
                    <th className="px-3 py-2.5 font-medium">Customer</th>
                    <th className="px-3 py-2.5 font-medium">Place</th>
                    <th className="px-3 py-2.5 font-medium">Category</th>
                    <th className="px-3 py-2.5 font-medium">SKU Code</th>
                    <th className="px-3 py-2.5 font-medium">Design</th>
                    <th className="px-3 py-2.5 font-medium">Shade</th>
                    <th className="px-3 py-2.5 font-medium">Size</th>
                    <th className="px-3 py-2.5 font-medium text-right">Allocated</th>
                    <th className="px-3 py-2.5 font-medium text-right">Packed</th>
                    <th className="px-3 py-2.5 font-medium text-right">Pending</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-3 py-2.5 font-medium">Generated On (IST)</th>
                    <th className="px-3 py-2.5 font-medium">DC Tagged</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={15} className="px-3 py-10 text-center text-[#5A5F6E]">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                    </td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={15} className="px-3 py-10 text-center text-[#5A5F6E]" data-testid="sppr-empty">
                      No pending seasonal SKU lines — everything generated is packed.
                    </td></tr>
                  ) : rows.map((r, i) => (
                    <tr key={`${r.pick_slip_number}-${r.sku_code}-${i}`} className="border-b hover:bg-gray-50" data-testid={`sppr-row-${i}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-lime-700" data-testid={`sppr-slip-${i}`}>{cell(r.pick_slip_number)}</td>
                      <td className="px-3 py-2 font-mono">{cell(r.slug)}</td>
                      <td className="px-3 py-2">{cell(r.customer)}</td>
                      <td className="px-3 py-2">{cell(r.short_name)}</td>
                      <td className="px-3 py-2">{cell(r.category)}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-[#1A1F2E]" data-testid={`sppr-sku-${i}`}>{cell(r.sku_code)}</td>
                      <td className="px-3 py-2">{cell(r.design_name)}</td>
                      <td className="px-3 py-2">{cell(r.shade)}</td>
                      <td className="px-3 py-2">{cell(r.size)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{r.allocated_qty}</td>
                      <td className="px-3 py-2 text-right">{r.packed_qty}</td>
                      <td className="px-3 py-2 text-right font-semibold text-amber-700">{r.pending_qty}</td>
                      <td className="px-3 py-2" data-testid={`sppr-status-${i}`}>
                        {r.status === 'Cancelled' ? (
                          <Badge className="bg-gray-200 text-gray-700 border-0 text-[11px]">Cancelled</Badge>
                        ) : r.status === 'Partial' ? (
                          <Badge className="bg-blue-100 text-blue-700 border-0 text-[11px]">Partial</Badge>
                        ) : (
                          <Badge className="bg-rose-100 text-rose-700 border-0 text-[11px]">Not Packed</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-[#5A5F6E]">{cell(r.generated_at_ist)}</td>
                      <td className="px-3 py-2 whitespace-nowrap" data-testid={`sppr-dc-${i}`}>
                        {r.dc_number ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px] font-mono">{r.dc_number}</Badge>
                        ) : r.dc_tagged ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px]">Tagged</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px]">Pending</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={() => goPage(page - 1)} disabled={page <= 1} data-testid="sppr-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#5A5F6E]" data-testid="sppr-page-info">Page {page} of {totalPages}</span>
            <Button variant="outline" onClick={() => goPage(page + 1)} disabled={page >= totalPages} data-testid="sppr-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SeasonalPendingPackingReport;
